import { useState } from 'react';

import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useSubmitCorrection } from '@/hooks/useJudgeTuning';

import type { FeedbackTargetType } from '@/lib/api/judge-tuning.api';

interface JudgeCorrectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callId: string;
  targetType: FeedbackTargetType;
  configId: string;
  configName: string;
  /** The judge's actual output, partitioned into booleans and other fields. */
  actualLabels: Record<string, boolean>;
  actualData: Record<string, unknown>;
}

/**
 * Dialog for the "Judge got this wrong" action. Shows the judge's actual
 * output, lets the user edit fields to what they should have been, and
 * submits a correction (+ optionally starts a tuning run).
 */
export const JudgeCorrectionDialog = ({
  open,
  onOpenChange,
  callId,
  targetType,
  configId,
  configName,
  actualLabels,
  actualData,
}: JudgeCorrectionDialogProps) => {
  // Editable copies of the judge's output.
  const [labels, setLabels] = useState<Record<string, boolean>>({});
  const [data, setData] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');
  const [includeOthers, setIncludeOthers] = useState(true);

  const submitCorrection = useSubmitCorrection(callId);

  // Initialize editable state when dialog opens.
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setLabels({ ...actualLabels });
      const dataStrings: Record<string, string> = {};
      for (const [key, value] of Object.entries(actualData)) {
        dataStrings[key] = typeof value === 'string' ? value : JSON.stringify(value);
      }
      setData(dataStrings);
      setReason('');
      setIncludeOthers(true);
    }
    onOpenChange(nextOpen);
  };

  const handleToggleLabel = (key: string) => {
    setLabels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDataChange = (key: string, value: string) => {
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (tune: boolean) => {
    // Build expected_labels: only include keys the user changed.
    const expectedLabels: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(labels)) {
      if (value !== actualLabels[key]) {
        expectedLabels[key] = value;
      }
    }

    // Build expected_data: only include keys the user changed.
    const expectedData: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      const original =
        typeof actualData[key] === 'string' ? actualData[key] : JSON.stringify(actualData[key]);
      if (value !== original) {
        // Try to parse as JSON, fall back to string.
        try {
          expectedData[key] = JSON.parse(value);
        } catch {
          expectedData[key] = value;
        }
      }
    }

    // If nothing changed and there's no note, show a validation error.
    // If nothing changed but there IS a note, submit with empty expected values
    // so the backend derives the correct ones from the note via LLM.
    if (Object.keys(expectedLabels).length === 0 && Object.keys(expectedData).length === 0) {
      if (!reason.trim()) {
        submitCorrection.reset();
        alert('Please flip at least one field or write a note explaining what is wrong');
        return;
      }
    }

    await submitCorrection.mutateAsync({
      call_id: callId,
      target_type: targetType,
      target_config_id: configId,
      expected_labels: expectedLabels,
      expected_data: expectedData,
      reason: reason.trim() || undefined,
      include_other_corrections: includeOthers,
      tune,
    });

    onOpenChange(false);
  };

  const isPending = submitCorrection.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Correct {targetType} output</DialogTitle>
          <DialogDescription>
            Edit fields to what they should have been for this call. Config:{' '}
            <span className="font-medium">{configName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Boolean labels */}
          {Object.keys(labels).length > 0 && (
            <div className="space-y-2">
              <div className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
                Labels
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {Object.entries(labels).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between gap-2">
                    <span className="truncate font-mono text-xs">{key}</span>
                    <Switch
                      checked={value}
                      onCheckedChange={() => handleToggleLabel(key)}
                      disabled={isPending}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data fields */}
          {Object.keys(data).length > 0 && (
            <div className="space-y-2">
              <div className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
                Data
              </div>
              <div className="space-y-2">
                {Object.entries(data).map(([key, value]) => (
                  <div key={key} className="space-y-1">
                    <span className="text-muted-foreground font-mono text-xs">{key}</span>
                    <Input
                      value={value}
                      onChange={(e) => handleDataChange(key, e.target.value)}
                      disabled={isPending}
                      className="h-7 text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reason */}
          <div className="space-y-1">
            <div className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
              Why is this wrong? (optional)
            </div>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Customer clearly said they were interested"
              disabled={isPending}
              className="h-7 text-xs"
            />
          </div>

          {/* Include other corrections checkbox */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="include-others"
              checked={includeOthers}
              onCheckedChange={(checked) => setIncludeOthers(checked === true)}
              disabled={isPending}
            />
            <span className="text-xs">Include other corrections for this config when tuning</span>
          </div>
        </div>

        {submitCorrection.isError && (
          <p className="text-destructive text-xs">
            {submitCorrection.error instanceof Error
              ? submitCorrection.error.message
              : 'Failed to submit correction'}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSubmit(false)}
            disabled={isPending}
          >
            Save correction
          </Button>
          <Button size="sm" onClick={() => handleSubmit(true)} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Submitting...
              </>
            ) : (
              'Save & tune'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
